import React from 'react'
import { WrapButton2 } from './Styled'
import { gTheme } from 'styles/theme';
import { LoadingButton } from '@mui/lab';
import { Tooltip } from '@mui/material';
import PermissionHOC from 'components/Permissions/PermissionHOC'

/**
 * Button (MUI LoadingButton wrapper)
 * Standard action button for the app. Supports loading state, custom variants,
 * tooltips, permissions (`PermissionHOC`), and theme-based styling via `WrapButton2`.
 *
 * Exported as `Button` from `components/Form`. Internal name: `Submit`.
 * Click handler signature: `action(props, event)` — full props are passed first.
 * `children` overrides the default `icon + label` layout.
 *
 * @param {Object} props
 * @param {Function} [props.action] - Click handler. Receives `(props, event)`.
 * @param {string} [props.label] - Button text (ignored when `children` is set).
 * @param {React.ReactNode} [props.children] - Custom content; replaces default layout.
 * @param {React.ReactNode} [props.icon] - Icon before label (default layout only).
 * @param {string} [props.variant='contained'] - Visual style: `contained`, `outlined`, `solid`, `solid2`, `light`, `regular`, `regular2`, `white`, `transparent`, `transparent2`, `danger`.
 * @param {string} [props.color] - MUI color token (e.g. `thirdly`, `secondary`, `dark`).
 * @param {string} [props.size] - Use `small` for compact styling.
 * @param {boolean} [props.loading] - Shows spinner and blocks clicks.
 * @param {boolean} [props.disabled] - Disabled when true or when `feature?.disabled` is true.
 * @param {boolean|Function} [props.hide] - Hides the button when truthy (supports a function).
 * @param {boolean} [props.fullWidth] - Stretch to container width.
 * @param {number|string} [props.width] - Fixed width.
 * @param {number|string} [props.height] - Fixed height.
 * @param {Object} [props.tooltip] - `{ title, placement? }` — defaults to `placement: 'left'`.
 * @param {Object} [props.feature] - Feature gate; `feature.disabled` disables the button.
 * @param {React.ElementType} [props.customComponent] - Root component for LoadingButton (e.g. router Link).
 * @param {string} [props.permissionHOC] - Permission wrapper key (e.g. `hidden`).
 * @param {string} [props.lightColor] - `gTheme` palette key for the `light` variant.
 * @param {string} [props.disabledColor] - `gTheme` palette key for disabled/outlined styles.
 * @param {string} [props.gridArea] - Grid area name; combined with `arrayIndex` as `${gridArea}_${arrayIndex}`.
 * @example
 * <Button label="Save" color="thirdly" action={handleSave} loading={isSaving} />
 * @example
 * <Button label="Delete" variant="danger" permissionHOC="hidden" action={handleDelete} />
 */
const Submit = (props) => {
    const { role, customComponent, tabIndex, size, lightColor, tooltip, border, blink, arrayIndex, gridArea, children, fontsize, className, systemUI = true, variant = 'contained', hide, width, autoFocus, loading, rightIcon, align, height, direction, disabledColor, icon, disabled, label, color, action, fullWidth, feature } = props;

    const isHidden =
        typeof hide === 'function'
            ? hide()
            : hide

    return (
        isHidden ||
        <WrapButton2
            size={size}
            fontsize={fontsize}
            systemUI={systemUI}
            fullWidth={fullWidth}
            blink={blink}
            gridArea={gridArea ? `${gridArea}_${arrayIndex}` : null}
            lightColor={gTheme[lightColor]}
            disabledcolor={gTheme[disabledColor]}
            disabled={feature?.disabled || disabled}
            direction={direction}
            align={align}
            height={height}
            width={width}
            variant={variant}
        >
            <Tooltip title={tooltip?.title} placement={tooltip?.placement || "left"}>
                <LoadingButton
                    className={`${className} ${color == "dark" && variant == "contained" && 'dark'} ${size == 'small' && 'small'} ${disabled && 'disabled'} ${variant == 'danger' && 'danger'} ${variant == 'solid2' && 'solid2'} ${variant == "outlined" && 'outlined'} ${variant == "solid" && 'solid'} ${variant == "light" && 'light'} ${fullWidth && 'fullWidth'} ${variant == "regular" && "regular"} ${variant == "white" && "white"} ${variant == "transparent" && "transparent"} ${variant == "regular2" && "regular2"} ${variant == "transparent2" && "transparent2"}`}
                    border={border}
                    onClick={(e) => action?.(props, e)}
                    autoFocus={autoFocus}
                    color={color}
                    disabled={feature?.disabled || disabled}
                    loading={loading}
                    variant={variant}
                    tabIndex={tabIndex}
                    role={role}
                    component={customComponent}
                >
                    {children ||
                        <>
                            {icon}
                            <div className='btn-text'>
                                {label}
                            </div>
                        </>
                    }
                </LoadingButton>
            </Tooltip>
        </WrapButton2>
    )
}

export default PermissionHOC(Submit)